import {
  Box,
  Card,
  Grid,
  IconButton,
  Popover,
  Stack,
  Typography,
} from "@mui/material";
import React, { useState } from "react";
import StarHalfIcon from "@mui/icons-material/StarHalf";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { BarChart, Bar, XAxis, Tooltip, ResponsiveContainer } from "recharts";
import { formatCount, ratingLabels } from "../../utils/utils";
import { GenericRechartsTooltip } from "../../components/CustomMUI";
import { useTheme } from "@emotion/react";

export default function Ratings({ data, isOwnProfile }) {
  const theme = useTheme();
  const [infoAnchorEl, setInfoAnchorEl] = useState(null);
  const handleInfoClick = (event) => {
    setInfoAnchorEl(event.currentTarget);
  };

  const isEmptyHistogram = data.ratings.histogram.every(
    (bucket) => bucket.count === 0
  );
  return (
    <>
      <Grid item xs={12} sm={6} container spacing={2}>
        <Grid item xs={12}>
          <Box
            sx={{
              p: 1,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              justifyContent: "center",
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center">
              <StarHalfIcon color="primary" />
              <Typography
                variant="h5"
                sx={{ fontWeight: "bold", lineHeight: 1 }}
              >
                {isOwnProfile ? "Your ratings..." : "Ratings..."}
              </Typography>
            </Stack>
            {/* <Typography
              variant="caption"
              sx={{
                lineHeight: 1.2,
                mt: 1,
                // textAlign: "center",
                // textWrap: "balance",
              }}
            >
              You watched {data.ratings.no_rating_count} movies without giving a
              rating - <u>rate now</u>
            </Typography> */}
          </Box>
        </Grid>
        <Grid item xs={4} sx={{ mt: -1 }}>
          <Card
            elevation={0}
            sx={{
              backgroundColor: (theme) => theme.palette.secondary.main,
              py: 1,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{ fontSize: 36, fontWeight: "bold", lineHeight: 1 }}
            >
              {formatCount(data.ratings.rating_count)}
            </Typography>
            <Typography
              variant="body2"
              sx={{ lineHeight: 1, textAlign: "center" }}
            >
              total <br /> ratings
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={4} sx={{ mt: -1 }}>
          <Card
            elevation={0}
            sx={{
              backgroundColor: (theme) => theme.palette.secondary.main,
              py: 1,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography
              sx={{
                fontSize: 36,
                fontWeight: "bold",
                lineHeight: 1,
                textAlign: "center",
              }}
            >
              {data.ratings.average ? data.ratings.average.toFixed(1) : "—"}
              <Typography
                component="span"
                sx={{
                  fontSize: 16, // Much smaller size
                  color: "text.secondary", // MUI default grey/disabled color
                  fontWeight: "medium", // Optional: slightly lighter weight
                }}
              >
                /5
              </Typography>
            </Typography>
            <Typography
              variant="body2"
              sx={{ lineHeight: 1, textAlign: "center" }}
            >
              average <br /> rating
            </Typography>
          </Card>
        </Grid>
        <Grid item xs={4} sx={{ mt: -1 }}>
          <Card
            elevation={0}
            sx={{
              backgroundColor: (theme) => theme.palette.secondary.main,
              py: 1,
              height: "100%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Stack direction="row" alignItems="flex-start">
              <Typography
                sx={{
                  fontSize: 36,
                  fontWeight: "bold",
                  lineHeight: 1,
                  textAlign: "center",
                }}
              >
                {data.ratings.std_dev ? data.ratings.std_dev.toFixed(1) : "—"}
              </Typography>
              <IconButton onClick={handleInfoClick} sx={{ p: 0 }}>
                <InfoOutlinedIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            </Stack>
            <Typography variant="body2" sx={{ lineHeight: 1 }}>
              standard <br />
              deviation
            </Typography>
          </Card>
        </Grid>
      </Grid>

      <Grid item xs={12} sm={6}>
        <Box
          sx={{
            minWidth: 0,
            flexGrow: 1,
            height: { xs: 120, sm: 150 },
            position: "relative",
            px: 1,
          }}
        >
          <ResponsiveContainer>
            <BarChart
              data={data.ratings.histogram}
              margin={{ top: 24, right: 0, bottom: 0, left: 0 }}
            >
              <XAxis
                dataKey="rating"
                tick={{ fontSize: 12 }}
                tickFormatter={(value) => `${value}★`}
                height={24}
              />
              {!isEmptyHistogram && (
                <Tooltip
                  cursor={{ fill: theme.palette.action.hover }}
                  content={
                    <GenericRechartsTooltip
                      renderContent={({ count, rating }) => {
                        const total = data.ratings.rating_count;
                        const percentage =
                          total > 0 ? Math.round((count / total) * 100) : 0;

                        return (
                          <>
                            {count} ({percentage}%) movies rated{" "}
                            {ratingLabels[rating]}
                          </>
                        );
                      }}
                    />
                  }
                />
              )}

              <Bar
                dataKey="count"
                fill={theme.palette.tertiary.main}
                radius={[
                  theme.shape.borderRadius,
                  theme.shape.borderRadius,
                  0,
                  0,
                ]}
              />
            </BarChart>
          </ResponsiveContainer>
          {isEmptyHistogram && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none", // important
                // backgroundColor: "background.paper",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontSize: 12,
                  color: "text.secondary",
                  textAlign: "center",
                  fontStyle: "italic",
                  transform: "translateY(-12px)",
                }}
              >
                No ratings data to show
              </Typography>
            </Box>
          )}
        </Box>
      </Grid>

      <Popover
        id="standard-deviation-popover"
        sx={{
          mt: 1,
        }}
        open={Boolean(infoAnchorEl)}
        anchorEl={infoAnchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        onClose={() => setInfoAnchorEl(null)}
        disableRestoreFocus={process.env.NODE_ENV === "development"}
      >
        <Box sx={{ p: 2 }} maxWidth={300}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Standard deviation shows how consistent your ratings are — values
            around <strong>0.3–0.6</strong> suggest fairly consistent ratings,
            while values above <strong>~1.0</strong> indicate much greater
            variation
          </Typography>
        </Box>
      </Popover>
    </>
  );
}
